import { useEffect, useRef } from "react";
import { useSession, useSessionManager } from "../store/session/SessionProvider";
import { percentile, telemetry, track } from "../lib/telemetry/telemetry";

/**
 * 세션이 서면 계측 큐에 보낼 곳(POST /api/telemetry)을 붙이고, 세션이 없으면 뗀다(모으기만).
 * AppLayout 이 한 번 부른다.
 */
export function useTelemetrySender() {
  const manager = useSessionManager();
  const state = useSession();
  const ready = state.status === "ready";
  useEffect(() => {
    if (!ready) return;
    telemetry.setSender(async (events) => {
      await manager.request<unknown>("/api/telemetry", {
        method: "POST",
        body: JSON.stringify({ events }),
      });
    });
    return () => telemetry.setSender(null);
  }, [manager, ready]);
}

/**
 * 3D 장면 성능 계측(perf) — 켜져 있는 동안 1초마다 fps 를 재고, 화면을 떠날 때(또는 탭이 가려질 때)
 * fps p5 · 틱 p95(커널 호스트 통계가 있으면) 를 한 번 보낸다. detail = 화면 키(gallery · course · play · replay).
 */
export function usePerfTelemetry(
  screen: string,
  active: boolean,
  tickP95?: () => Promise<number | null>,
) {
  const tickRef = useRef(tickP95);
  tickRef.current = tickP95;
  useEffect(() => {
    if (!active || typeof requestAnimationFrame === "undefined") return;
    const fps: number[] = [];
    let frames = 0;
    let since = performance.now();
    let raf = 0;
    const loop = (ts: number) => {
      frames += 1;
      if (ts - since >= 1000) {
        fps.push((frames * 1000) / (ts - since));
        frames = 0;
        since = ts;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    let sent = false;
    const report = () => {
      if (sent) return;
      sent = true;
      const p5 = percentile(fps, 5);
      if (p5 != null) track({ t: "perf", code: "fps-p5", value: p5, detail: screen });
      const tick = tickRef.current?.();
      if (tick)
        void tick
          .then((v) => {
            if (v != null) track({ t: "perf", code: "tick-p95", value: v, detail: screen });
          })
          .catch(() => {});
    };
    const onHide = () => document.visibilityState === "hidden" && report();
    document.addEventListener("visibilitychange", onHide);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", onHide);
      report();
    };
  }, [screen, active]);
}
